import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { ContentBlockType } from '@onesource/schemas';
import { FC } from 'react';
import { MdVideocam } from 'react-icons/md';

import { LessonBreadcrumbs } from '#/features/community-hub/components/lesson-breadcrumbs';

import { useResolvedBlock } from './use-resolved-block';

type TEventItemViewProps = {
  /** URL slug of the content block. */
  itemSlug: string;
};

/** Event content block viewer. Shared by the program-level and lesson-level routes. */
export const EventItemView: FC<TEventItemViewProps> = ({ itemSlug }) => {
  const {
    programId,
    moduleId,
    lessonId,
    programSlug,
    moduleSlug,
    lessonSlug,
    lessonBasePath,
    lesson,
    module,
    findParentLesson,
    block: eventBlock,
    isLoading: isLoadingLesson,
    error: lessonError,
  } = useResolvedBlock(itemSlug, ContentBlockType.EVENT);

  // The Zoom meeting is joined on a dedicated page (`/meeting.html`); we hand off to it
  // and come back to this lesson when the user leaves the meeting.
  const lessonPath = lessonBasePath;
  const meetingHref = `/meeting.html?c=${encodeURIComponent(eventBlock?.id ?? '')}&return=${encodeURIComponent(lessonPath)}`;

  if (isLoadingLesson) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress />
      </Box>
    );
  }

  if (lessonError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load lesson: {lessonError.message}</Alert>
      </Box>
    );
  }

  if (!eventBlock) {
    return (
      <Box p={3}>
        <Alert severity="error">Event content not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <LessonBreadcrumbs
        spaceId={programId}
        moduleId={moduleId}
        lessonId={lessonId}
        module={module}
        lesson={lesson}
        parentLesson={findParentLesson}
        currentLabel="Zoom Session"
        programSlug={programSlug}
        moduleSlug={moduleSlug}
        lessonSlug={lessonSlug}
      />

      <Box
        sx={{
          mt: 2,
          background: 'white',
          borderRadius: 2,
          width: { xs: '100%', md: 'calc(100% - 190px)' },
          p: { xs: 3, md: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          textAlign: 'center',
        }}
      >
        <MdVideocam size={40} color="#2D8CFF" />
        <Typography variant="h6" fontWeight={700}>
          Live Zoom Session
        </Typography>
        <Typography variant="body2" color="text.secondary" maxWidth={420}>
          The session opens in a dedicated meeting view so gallery view and spotlight work correctly. You'll return here
          when you leave the meeting.
        </Typography>
        <Button
          component="a"
          href={meetingHref}
          variant="contained"
          size="large"
          startIcon={<MdVideocam />}
          sx={{ mt: 1, py: 1.5, px: 4, bgcolor: '#2D8CFF', '&:hover': { bgcolor: '#1a7ae0' } }}
        >
          Open Meeting
        </Button>
      </Box>
    </Box>
  );
};
